import { useCallback, useRef, useEffect } from 'react';
import { useApp } from '../context';

// Sound types available in the app
export type SoundType =
  | 'pointEarn'      // NC points earned
  | 'levelUp'        // Level up celebration
  | 'achievement'    // Achievement unlocked
  | 'complete'       // Module/task completed
  | 'click'          // Button click feedback
  | 'error'          // Error/warning sound
  | 'notification'   // General notification
  | 'breathing'      // Breathing exercise tone
  | 'success';       // General success

// Audio context singleton
let audioContext: AudioContext | null = null;

function getAudioContext(): AudioContext {
  if (!audioContext) {
    audioContext = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
  }
  return audioContext;
}

// Generate a simple tone
function playTone(
  frequency: number,
  duration: number,
  type: OscillatorType = 'sine',
  volume: number = 0.3
): void {
  const ctx = getAudioContext();

  // Resume context if suspended (browser autoplay policy)
  if (ctx.state === 'suspended') {
    ctx.resume();
  }

  const oscillator = ctx.createOscillator();
  const gainNode = ctx.createGain();

  oscillator.type = type;
  oscillator.frequency.setValueAtTime(frequency, ctx.currentTime);

  // Envelope for smooth sound
  gainNode.gain.setValueAtTime(0, ctx.currentTime);
  gainNode.gain.linearRampToValueAtTime(volume, ctx.currentTime + 0.01);
  gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + duration);

  oscillator.connect(gainNode);
  gainNode.connect(ctx.destination);

  oscillator.start(ctx.currentTime);
  oscillator.stop(ctx.currentTime + duration);
}

// Play a sequence of tones
function playSequence(
  notes: Array<{ freq: number; duration: number; delay?: number }>,
  type: OscillatorType = 'sine',
  volume: number = 0.3
): void {
  let currentDelay = 0;
  notes.forEach((note) => {
    setTimeout(() => {
      playTone(note.freq, note.duration, type, volume);
    }, currentDelay * 1000);
    currentDelay += note.delay ?? note.duration;
  });
}

// Sound definitions
const SOUNDS: Record<SoundType, () => void> = {
  pointEarn: () => {
    // Short upward chime
    playSequence([
      { freq: 880, duration: 0.08 },
      { freq: 1108, duration: 0.1 },
    ], 'sine', 0.2);
  },

  levelUp: () => {
    // Triumphant arpeggio
    playSequence([
      { freq: 523, duration: 0.1 },  // C5
      { freq: 659, duration: 0.1 },  // E5
      { freq: 784, duration: 0.1 },  // G5
      { freq: 1047, duration: 0.3 }, // C6
    ], 'sine', 0.25);
  },

  achievement: () => {
    // Celebratory fanfare
    playSequence([
      { freq: 523, duration: 0.12 },  // C5
      { freq: 659, duration: 0.12 },  // E5
      { freq: 784, duration: 0.12 },  // G5
      { freq: 1047, duration: 0.15 }, // C6
      { freq: 784, duration: 0.1 },   // G5
      { freq: 1047, duration: 0.4 },  // C6
    ], 'sine', 0.3);
  },

  complete: () => {
    // Satisfying completion sound
    playSequence([
      { freq: 440, duration: 0.1 },
      { freq: 554, duration: 0.1 },
      { freq: 659, duration: 0.2 },
    ], 'sine', 0.2);
  },

  click: () => {
    // Subtle click
    playTone(800, 0.05, 'sine', 0.1);
  },

  error: () => {
    // Low warning tone
    playSequence([
      { freq: 200, duration: 0.15 },
      { freq: 150, duration: 0.2 },
    ], 'sawtooth', 0.15);
  },

  notification: () => {
    // Gentle notification
    playSequence([
      { freq: 587, duration: 0.1 },  // D5
      { freq: 880, duration: 0.15 }, // A5
    ], 'sine', 0.2);
  },

  breathing: () => {
    // Soft breathing cue
    playTone(396, 0.5, 'sine', 0.15);
  },

  success: () => {
    // General success tone
    playSequence([
      { freq: 523, duration: 0.1 },
      { freq: 784, duration: 0.2 },
    ], 'sine', 0.2);
  },
};

// Main hook
export function useSound() {
  const { state } = useApp();
  const soundEnabled = state.settings.soundEnabled;
  const lastSoundRef = useRef<number>(0);
  const MIN_INTERVAL = 50; // Minimum ms between sounds to prevent spam

  // Play a sound by type
  const playSound = useCallback((type: SoundType) => {
    if (!soundEnabled) return;

    const now = Date.now();
    if (now - lastSoundRef.current < MIN_INTERVAL) return;
    lastSoundRef.current = now;

    try {
      SOUNDS[type]();
    } catch (error) {
      console.warn('[Sound] Failed to play sound:', error);
    }
  }, [soundEnabled]);

  // Play point earning sound with intensity based on points
  const playPointSound = useCallback((points: number) => {
    if (!soundEnabled) return;

    if (points >= 100) {
      playSound('levelUp');
    } else if (points >= 50) {
      playSound('complete');
    } else {
      playSound('pointEarn');
    }
  }, [soundEnabled, playSound]);

  return {
    playSound,
    playPointSound,
    soundEnabled,
  };
}

// Hook for breathing exercise with rhythm
export function useBreathingSound() {
  const { state } = useApp();
  const soundEnabled = state.settings.soundEnabled;
  const intervalRef = useRef<number | null>(null);

  const startBreathingCue = useCallback((intervalMs: number = 4000) => {
    if (!soundEnabled) return;

    // Play initial cue
    SOUNDS.breathing();

    // Set up interval
    intervalRef.current = window.setInterval(() => {
      SOUNDS.breathing();
    }, intervalMs);
  }, [soundEnabled]);

  const stopBreathingCue = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  return {
    startBreathingCue,
    stopBreathingCue,
  };
}
